import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '@/hooks/useAuth';
import SoulEditor, { Soul, DEFAULT_SOUL } from '../components/SoulEditor';
import DocumentUploader, { UploadedDoc } from '../components/DocumentUploader';
import ChatPreview from '../components/ChatPreview';

/* ---- Constants ---- */
const INDUSTRIES = [
  'dental', 'plumbing', 'hvac', 'roofing', 'insurance', 'real-estate', 'restaurant',
  'salon', 'auto-repair', 'legal', 'accounting', 'marketing', 'ecommerce', 'fitness',
  'healthcare', 'veterinary', 'landscaping', 'cleaning', 'photography', 'consulting',
  'education', 'construction', 'financial', 'hospitality', 'general',
] as const;

const AVATAR_COLORS = [
  '#39ff14', '#3d7eff', '#a855f7', '#f59e0b', '#ef4444', '#06b6d4',
];

const STEPS = [
  { num: 1, label: 'Identity' },
  { num: 2, label: 'Personality' },
  { num: 3, label: 'Knowledge' },
  { num: 4, label: 'Channels' },
  { num: 5, label: 'Launch' },
];

/* ---- Channel Config Types ---- */
interface ChannelConfig {
  webchat: { enabled: boolean };
  phone: { enabled: boolean; apiKey: string; phoneNumber: string };
  sms: { enabled: boolean; accountSid: string; authToken: string; phoneNumber: string };
  telegram: { enabled: boolean; botToken: string };
  whatsapp: { enabled: boolean; accountId: string; phoneId: string; token: string };
  discord: { enabled: boolean };
  slack: { enabled: boolean };
}

/* ---- Q&A Pair ---- */
interface QAPair {
  id: string;
  question: string;
  answer: string;
}

/* ---- Onboarding State ---- */
interface OnboardingData {
  // Step 1
  botName: string;
  businessName: string;
  industry: string;
  avatarColor: string;
  // Step 2
  soul: Soul;
  // Step 3
  documents: UploadedDoc[];
  services: string[];
  serviceInput: string;
  pricing: string;
  team: string;
  qaPairs: QAPair[];
  // Step 4
  channels: ChannelConfig;
}

const initialData: OnboardingData = {
  botName: '',
  businessName: '',
  industry: '',
  avatarColor: AVATAR_COLORS[0],
  soul: DEFAULT_SOUL,
  documents: [],
  services: [],
  serviceInput: '',
  pricing: '',
  team: '',
  qaPairs: [{ id: '1', question: '', answer: '' }],
  channels: {
    webchat: { enabled: true },
    phone: { enabled: false, apiKey: '', phoneNumber: '' },
    sms: { enabled: false, accountSid: '', authToken: '', phoneNumber: '' },
    telegram: { enabled: false, botToken: '' },
    whatsapp: { enabled: false, accountId: '', phoneId: '', token: '' },
    discord: { enabled: false },
    slack: { enabled: false },
  },
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { refetchUser } = useAuth();
  const [step, setStep] = useState(1);
  const [data, setData] = useState<OnboardingData>(initialData);
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');

  const update = <K extends keyof OnboardingData>(key: K, value: OnboardingData[K]) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  /* ---- Step navigation ---- */
  const saveStep = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api.post(`/onboarding/step/${step}`, data);
      if (!res.ok) {
        setError(res.error || 'Failed to save');
        setSaving(false);
        return false;
      }
      setSaving(false);
      return true;
    } catch {
      setError('Failed to save step');
      setSaving(false);
      return false;
    }
  }, [step, data]);

  const goNext = async () => {
    const saved = await saveStep();
    if (saved && step < 5) setStep(step + 1);
  };

  const goBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const goLive = async () => {
    setLaunching(true);
    setError('');
    const res = await api.post('/onboarding/complete', data);
    if (res.ok) {
      // Hard reload to / — ensures completely fresh auth state
      // (React Router navigate can race with useAuth re-fetch)
      window.location.replace('/');
    } else {
      setError(res.error || 'Failed to launch');
      setLaunching(false);
    }
  };

  /* ---- Service tag input ---- */
  const addService = () => {
    const val = data.serviceInput.trim();
    if (val && !data.services.includes(val)) {
      update('services', [...data.services, val]);
      update('serviceInput', '');
    }
  };

  const removeService = (s: string) => {
    update('services', data.services.filter((x) => x !== s));
  };

  /* ---- Q&A helpers ---- */
  const addQA = () => {
    update('qaPairs', [
      ...data.qaPairs,
      { id: `${Date.now()}`, question: '', answer: '' },
    ]);
  };

  const updateQA = (id: string, field: 'question' | 'answer', value: string) => {
    update(
      'qaPairs',
      data.qaPairs.map((qa) => (qa.id === id ? { ...qa, [field]: value } : qa))
    );
  };

  const removeQA = (id: string) => {
    if (data.qaPairs.length <= 1) return;
    update('qaPairs', data.qaPairs.filter((qa) => qa.id !== id));
  };

  /* ---- Validation ---- */
  const canProceed = () => {
    switch (step) {
      case 1:
        return data.botName.trim() && data.businessName.trim() && data.industry;
      case 2:
        return data.soul.tone && data.soul.instructions.trim();
      case 3:
        return true; // Knowledge is optional
      case 4:
        return true; // At least webchat is enabled by default
      case 5:
        return true;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-[100dvh] bg-surface-primary">
      {/* Header */}
      <header className="border-b border-border bg-surface-secondary/50 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#39ff14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            <span className="text-sm font-bold gradient-text tracking-wider">ZEUS</span>
          </div>
          <p className="text-xs text-text-secondary">Give your AI employee a soul</p>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="border-b border-border bg-surface-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => (
              <div key={s.num} className="flex items-center flex-1 last:flex-initial">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                      step > s.num
                        ? 'bg-accent border-accent text-black'
                        : step === s.num
                        ? 'border-accent text-accent bg-accent/10 animate-pulse-glow'
                        : 'border-border text-text-muted bg-surface-tertiary'
                    }`}
                  >
                    {step > s.num ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      s.num
                    )}
                  </div>
                  <span
                    className={`text-[10px] mt-1.5 font-medium hidden sm:block ${
                      step >= s.num ? 'text-text-primary' : 'text-text-muted'
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 mx-2 sm:mx-4">
                    <div className="h-0.5 bg-border rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent rounded-full transition-all duration-500"
                        style={{ width: step > s.num ? '100%' : '0%' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-card px-4 py-3 text-sm text-red-400 mb-6 animate-fade-in">
            {error}
          </div>
        )}

        <div className="animate-slide-in" key={step}>
          {/* ============ STEP 1: Identity ============ */}
          {step === 1 && (
            <div className="space-y-8">
              <div>
                <h2 className="text-xl font-bold text-text-primary mb-1">Name & Identity</h2>
                <p className="text-sm text-text-secondary">
                  Give your AI employee a name and identity. This is how it will introduce itself to your customers.
                </p>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
                    AI Employee Name
                  </label>
                  <input
                    type="text"
                    value={data.botName}
                    onChange={(e) => update('botName', e.target.value)}
                    className="input-base w-full h-10"
                    placeholder="e.g., Sarah, Max, Alex..."
                    autoFocus
                  />
                  <p className="text-xs text-text-muted mt-1">
                    Pick a name that fits your brand personality
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
                    Business Name
                  </label>
                  <input
                    type="text"
                    value={data.businessName}
                    onChange={(e) => update('businessName', e.target.value)}
                    className="input-base w-full h-10"
                    placeholder="e.g., Bright Smiles Dental"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
                  Industry
                </label>
                <select
                  value={data.industry}
                  onChange={(e) => update('industry', e.target.value)}
                  className="input-base w-full h-10 sm:w-72"
                >
                  <option value="">Select your industry...</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>
                      {ind.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider mb-3">
                  Avatar Color
                </label>
                <div className="flex gap-3 flex-wrap">
                  {AVATAR_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => update('avatarColor', color)}
                      className={`w-12 h-12 rounded-lg flex items-center justify-center text-sm font-bold border-2 transition-all ${
                        data.avatarColor === color
                          ? 'border-white/40 scale-110 shadow-lg'
                          : 'border-transparent hover:border-white/20'
                      }`}
                      style={{ backgroundColor: `${color}22`, borderColor: data.avatarColor === color ? color : undefined }}
                    >
                      <span style={{ color }}>
                        {data.botName ? data.botName[0]?.toUpperCase() : 'Z'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              {data.botName && (
                <div className="card-base p-4 flex items-center gap-4 animate-fade-in">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold"
                    style={{ backgroundColor: `${data.avatarColor}22`, color: data.avatarColor }}
                  >
                    {data.botName[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-text-primary">{data.botName}</p>
                    <p className="text-xs text-text-secondary">
                      AI Employee at {data.businessName || 'Your Business'}
                    </p>
                  </div>
                  <span className="badge badge-green ml-auto">Preview</span>
                </div>
              )}
            </div>
          )}

          {/* ============ STEP 2: Personality ============ */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-text-primary mb-1">Soul & Personality</h2>
                <p className="text-sm text-text-secondary">
                  Define how {data.botName || 'your AI'} thinks, speaks, and handles conversations. This is the soul of your AI employee.
                </p>
              </div>

              <SoulEditor soul={data.soul} onChange={(soul) => update('soul', soul)} />
            </div>
          )}

          {/* ============ STEP 3: Knowledge ============ */}
          {step === 3 && (
            <div className="space-y-8">
              <div>
                <h2 className="text-xl font-bold text-text-primary mb-1">Memory & Knowledge</h2>
                <p className="text-sm text-text-secondary">
                  Upload documents and add business information so {data.botName || 'your AI'} can answer customer questions accurately.
                </p>
              </div>

              {/* Document Upload */}
              <div>
                <h3 className="text-sm font-semibold text-text-primary mb-3">Documents</h3>
                <DocumentUploader
                  documents={data.documents}
                  onDocumentsChange={(docs) => update('documents', docs as UploadedDoc[])}
                />
              </div>

              {/* Business Info */}
              <div className="space-y-5">
                <h3 className="text-sm font-semibold text-text-primary">Business Information</h3>

                {/* Services (tag input) */}
                <div>
                  <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
                    Services / Products
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={data.serviceInput}
                      onChange={(e) => update('serviceInput', e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addService();
                        }
                      }}
                      className="input-base flex-1 h-9"
                      placeholder="Type a service and press Enter..."
                    />
                    <button type="button" onClick={addService} className="btn-secondary h-9 px-3 text-xs">
                      Add
                    </button>
                  </div>
                  {data.services.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {data.services.map((s) => (
                        <span
                          key={s}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs"
                        >
                          {s}
                          <button
                            type="button"
                            onClick={() => removeService(s)}
                            className="hover:text-white transition-colors"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pricing */}
                <div>
                  <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
                    Pricing Information
                  </label>
                  <textarea
                    value={data.pricing}
                    onChange={(e) => update('pricing', e.target.value)}
                    className="input-base w-full h-24 resize-y"
                    placeholder="Describe your pricing structure, packages, or rates..."
                  />
                </div>

                {/* Team */}
                <div>
                  <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
                    Team / Staff
                  </label>
                  <textarea
                    value={data.team}
                    onChange={(e) => update('team', e.target.value)}
                    className="input-base w-full h-24 resize-y"
                    placeholder="List team members and their roles so your AI knows who to reference..."
                  />
                </div>
              </div>

              {/* Q&A Builder */}
              <div>
                <h3 className="text-sm font-semibold text-text-primary mb-3">Q&A Builder</h3>
                <p className="text-xs text-text-secondary mb-3">
                  Add common questions and their answers. Your AI will use these as a primary source.
                </p>
                <div className="space-y-3">
                  {data.qaPairs.map((qa, i) => (
                    <div key={qa.id} className="card-base p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-text-muted font-medium">Q&A #{i + 1}</span>
                        {data.qaPairs.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeQA(qa.id)}
                            className="text-text-muted hover:text-red-400 text-xs transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        value={qa.question}
                        onChange={(e) => updateQA(qa.id, 'question', e.target.value)}
                        className="input-base w-full h-9"
                        placeholder="Question (e.g., What are your hours?)"
                      />
                      <textarea
                        value={qa.answer}
                        onChange={(e) => updateQA(qa.id, 'answer', e.target.value)}
                        className="input-base w-full h-20 resize-y"
                        placeholder="Answer..."
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addQA}
                    className="text-xs text-accent hover:text-accent-dim font-medium flex items-center gap-1"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add Q&A Pair
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ============ STEP 4: Channels ============ */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-text-primary mb-1">Connect Channels</h2>
                <p className="text-sm text-text-secondary">
                  Choose where {data.botName || 'your AI'} will communicate with customers. You can always add more later.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {/* Web Chat */}
                <ChannelCard
                  icon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  }
                  name="Web Chat"
                  description="Embed a chat widget on your website. Always available."
                  connected={data.channels.webchat.enabled}
                  alwaysOn
                >
                  <div className="mt-3 p-3 bg-surface-primary rounded-input border border-border">
                    <p className="text-[10px] text-text-muted mb-1 uppercase tracking-wider font-medium">Embed Code Preview</p>
                    <code className="text-xs text-accent-blue break-all">
                      {'<script src="https://zeus.ai/widget.js" data-bot="' + (data.botName || 'bot') + '"></script>'}
                    </code>
                  </div>
                </ChannelCard>

                {/* Phone (VAPI) */}
                <ChannelCard
                  icon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  }
                  name="Phone Calls (VAPI)"
                  description="Connect your own VAPI account for AI-powered phone calls."
                  connected={data.channels.phone.enabled}
                  onToggle={(v) =>
                    update('channels', { ...data.channels, phone: { ...data.channels.phone, enabled: v } })
                  }
                >
                  {data.channels.phone.enabled && (
                    <div className="mt-3 space-y-2">
                      <input
                        type="text"
                        value={data.channels.phone.apiKey}
                        onChange={(e) =>
                          update('channels', {
                            ...data.channels,
                            phone: { ...data.channels.phone, apiKey: e.target.value },
                          })
                        }
                        className="input-base w-full h-8 text-xs"
                        placeholder="Your VAPI API Key (from dashboard.vapi.ai)"
                      />
                      <input
                        type="text"
                        value={data.channels.phone.phoneNumber}
                        onChange={(e) =>
                          update('channels', {
                            ...data.channels,
                            phone: { ...data.channels.phone, phoneNumber: e.target.value },
                          })
                        }
                        className="input-base w-full h-8 text-xs"
                        placeholder="VAPI phone number (e.g., +1234567890)"
                      />
                    </div>
                  )}
                </ChannelCard>

                {/* SMS (Twilio) */}
                <ChannelCard
                  icon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      <line x1="9" y1="10" x2="15" y2="10" />
                    </svg>
                  }
                  name="SMS (Twilio)"
                  description="Connect your own Twilio account to send and receive SMS."
                  connected={data.channels.sms.enabled}
                  onToggle={(v) =>
                    update('channels', { ...data.channels, sms: { ...data.channels.sms, enabled: v } })
                  }
                >
                  {data.channels.sms.enabled && (
                    <div className="mt-3 space-y-2">
                      <input
                        type="text"
                        value={data.channels.sms.accountSid}
                        onChange={(e) =>
                          update('channels', {
                            ...data.channels,
                            sms: { ...data.channels.sms, accountSid: e.target.value },
                          })
                        }
                        className="input-base w-full h-8 text-xs"
                        placeholder="Twilio Account SID"
                      />
                      <input
                        type="password"
                        value={data.channels.sms.authToken}
                        onChange={(e) =>
                          update('channels', {
                            ...data.channels,
                            sms: { ...data.channels.sms, authToken: e.target.value },
                          })
                        }
                        className="input-base w-full h-8 text-xs"
                        placeholder="Twilio Auth Token"
                      />
                      <input
                        type="text"
                        value={data.channels.sms.phoneNumber}
                        onChange={(e) =>
                          update('channels', {
                            ...data.channels,
                            sms: { ...data.channels.sms, phoneNumber: e.target.value },
                          })
                        }
                        className="input-base w-full h-8 text-xs"
                        placeholder="Twilio phone number (e.g., +1234567890)"
                      />
                    </div>
                  )}
                </ChannelCard>

                {/* Telegram */}
                <ChannelCard
                  icon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21.198 2.433a2.242 2.242 0 0 0-1.022.215l-17.2 7.288a1.5 1.5 0 0 0 .175 2.826l4.55 1.248 1.675 5.85a1.12 1.12 0 0 0 1.82.455l2.466-2.283 4.578 3.378a1.5 1.5 0 0 0 2.36-1.09L22.95 3.88a1.742 1.742 0 0 0-1.752-1.447z" />
                    </svg>
                  }
                  name="Telegram"
                  description="Connect a Telegram bot to handle messages."
                  connected={data.channels.telegram.enabled}
                  onToggle={(v) =>
                    update('channels', {
                      ...data.channels,
                      telegram: { ...data.channels.telegram, enabled: v },
                    })
                  }
                >
                  {data.channels.telegram.enabled && (
                    <div className="mt-3">
                      <input
                        type="text"
                        value={data.channels.telegram.botToken}
                        onChange={(e) =>
                          update('channels', {
                            ...data.channels,
                            telegram: { ...data.channels.telegram, botToken: e.target.value },
                          })
                        }
                        className="input-base w-full h-8 text-xs"
                        placeholder="Bot token from @BotFather"
                      />
                    </div>
                  )}
                </ChannelCard>

                {/* WhatsApp */}
                <ChannelCard
                  icon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                    </svg>
                  }
                  name="WhatsApp"
                  description="Handle WhatsApp Business conversations."
                  connected={data.channels.whatsapp.enabled}
                  onToggle={(v) =>
                    update('channels', {
                      ...data.channels,
                      whatsapp: { ...data.channels.whatsapp, enabled: v },
                    })
                  }
                >
                  {data.channels.whatsapp.enabled && (
                    <div className="mt-3 space-y-2">
                      <input
                        type="text"
                        value={data.channels.whatsapp.accountId}
                        onChange={(e) =>
                          update('channels', {
                            ...data.channels,
                            whatsapp: { ...data.channels.whatsapp, accountId: e.target.value },
                          })
                        }
                        className="input-base w-full h-8 text-xs"
                        placeholder="Business Account ID"
                      />
                      <input
                        type="text"
                        value={data.channels.whatsapp.phoneId}
                        onChange={(e) =>
                          update('channels', {
                            ...data.channels,
                            whatsapp: { ...data.channels.whatsapp, phoneId: e.target.value },
                          })
                        }
                        className="input-base w-full h-8 text-xs"
                        placeholder="Phone Number ID"
                      />
                      <input
                        type="text"
                        value={data.channels.whatsapp.token}
                        onChange={(e) =>
                          update('channels', {
                            ...data.channels,
                            whatsapp: { ...data.channels.whatsapp, token: e.target.value },
                          })
                        }
                        className="input-base w-full h-8 text-xs"
                        placeholder="Access Token"
                      />
                    </div>
                  )}
                </ChannelCard>

                {/* Discord */}
                <ChannelCard
                  icon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="9" cy="12" r="1" />
                      <circle cx="15" cy="12" r="1" />
                      <path d="M7.5 7.5c3.5-1 5.5-1 9 0" />
                      <path d="M7 16.5c3.5 1 6.5 1 10 0" />
                      <path d="M15.5 17c0 1 1.5 3 2 3 1.5 0 2.833-1.667 3.5-3 .667-1.333.5-5.833-1.5-11.5-1.457-1.015-3-1.34-4.5-1.5l-1 2.5" />
                      <path d="M8.5 17c0 1-1.356 3-1.832 3-1.429 0-2.698-1.667-3.333-3-.635-1.333-.476-5.833 1.428-11.5C6.151 4.485 7.545 4.16 9 4l1 2.5" />
                    </svg>
                  }
                  name="Discord"
                  description="Add your AI bot to Discord servers."
                  connected={data.channels.discord.enabled}
                  onToggle={(v) =>
                    update('channels', {
                      ...data.channels,
                      discord: { ...data.channels.discord, enabled: v },
                    })
                  }
                >
                  {data.channels.discord.enabled && (
                    <div className="mt-3">
                      <button type="button" className="btn-secondary text-xs w-full h-8">
                        Connect with Discord OAuth
                      </button>
                    </div>
                  )}
                </ChannelCard>

                {/* Slack */}
                <ChannelCard
                  icon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="13" y="2" width="3" height="8" rx="1.5" />
                      <path d="M19 8.5V10h1.5A1.5 1.5 0 1 0 19 8.5" />
                      <rect x="8" y="14" width="3" height="8" rx="1.5" />
                      <path d="M5 15.5V14H3.5A1.5 1.5 0 1 0 5 15.5" />
                      <rect x="14" y="13" width="8" height="3" rx="1.5" />
                      <path d="M15.5 19H14v1.5a1.5 1.5 0 1 0 1.5-1.5" />
                      <rect x="2" y="8" width="8" height="3" rx="1.5" />
                      <path d="M8.5 5H10V3.5A1.5 1.5 0 1 0 8.5 5" />
                    </svg>
                  }
                  name="Slack"
                  description="Deploy your AI into Slack workspaces."
                  connected={data.channels.slack.enabled}
                  onToggle={(v) =>
                    update('channels', {
                      ...data.channels,
                      slack: { ...data.channels.slack, enabled: v },
                    })
                  }
                >
                  {data.channels.slack.enabled && (
                    <div className="mt-3">
                      <button type="button" className="btn-secondary text-xs w-full h-8">
                        Add to Slack
                      </button>
                    </div>
                  )}
                </ChannelCard>
              </div>
            </div>
          )}

          {/* ============ STEP 5: Test & Launch ============ */}
          {step === 5 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-text-primary mb-1">Test & Launch</h2>
                <p className="text-sm text-text-secondary">
                  Review your configuration and test {data.botName || 'your AI'} before going live.
                </p>
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                {/* Summary */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-text-primary">Configuration Summary</h3>

                  <div className="card-base p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-base font-bold"
                        style={{ backgroundColor: `${data.avatarColor}22`, color: data.avatarColor }}
                      >
                        {data.botName?.[0]?.toUpperCase() || 'Z'}
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{data.botName || 'Unnamed Bot'}</p>
                        <p className="text-xs text-text-secondary">
                          {data.businessName} | {data.industry?.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'No industry'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-base p-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-text-secondary uppercase tracking-wider">Tone</span>
                      <span className="text-xs text-text-primary font-medium capitalize">{data.soul.tone}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-text-secondary uppercase tracking-wider">Do's</span>
                      <span className="text-xs text-text-primary">{data.soul.dos.filter(Boolean).length} rules</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-text-secondary uppercase tracking-wider">Don'ts</span>
                      <span className="text-xs text-text-primary">{data.soul.donts.filter(Boolean).length} rules</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-text-secondary uppercase tracking-wider">Documents</span>
                      <span className="text-xs text-text-primary">{data.documents.length} files</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-text-secondary uppercase tracking-wider">Q&A Pairs</span>
                      <span className="text-xs text-text-primary">{data.qaPairs.filter((q) => q.question).length} pairs</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-text-secondary uppercase tracking-wider">Services</span>
                      <span className="text-xs text-text-primary">{data.services.length} listed</span>
                    </div>
                  </div>

                  {/* Instructions preview */}
                  {data.soul.instructions && (
                    <div className="card-base p-4">
                      <p className="text-xs text-text-secondary uppercase tracking-wider mb-2">Instructions Preview</p>
                      <p className="text-xs text-text-primary leading-relaxed line-clamp-4">
                        {data.soul.instructions}
                      </p>
                    </div>
                  )}

                  {/* Connected channels */}
                  <div className="card-base p-4">
                    <p className="text-xs text-text-secondary uppercase tracking-wider mb-2">Connected Channels</p>
                    <div className="flex flex-wrap gap-2">
                      {data.channels.webchat.enabled && <span className="badge badge-green">Web Chat</span>}
                      {data.channels.phone.enabled && <span className="badge badge-green">Phone (VAPI)</span>}
                      {data.channels.sms.enabled && <span className="badge badge-green">SMS (Twilio)</span>}
                      {data.channels.telegram.enabled && <span className="badge badge-green">Telegram</span>}
                      {data.channels.whatsapp.enabled && <span className="badge badge-green">WhatsApp</span>}
                      {data.channels.discord.enabled && <span className="badge badge-green">Discord</span>}
                      {data.channels.slack.enabled && <span className="badge badge-green">Slack</span>}
                    </div>
                  </div>
                </div>

                {/* Chat Preview */}
                <div>
                  <h3 className="text-sm font-semibold text-text-primary mb-3">Live Chat Preview</h3>
                  <ChatPreview
                    botName={data.botName || 'Zeus Bot'}
                    greeting={`Hi! I'm ${data.botName || 'your AI assistant'} from ${data.businessName || 'your business'}. How can I help you today?`}
                  />
                </div>
              </div>

              {/* Go Live Button */}
              <div className="border-t border-border pt-6 mt-8">
                <div className="text-center">
                  <button
                    type="button"
                    onClick={goLive}
                    disabled={launching}
                    className="btn-primary px-8 py-3 text-base font-bold animate-pulse-glow"
                  >
                    {launching ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        Launching...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                        </svg>
                        Go Live
                      </span>
                    )}
                  </button>
                  <p className="text-xs text-text-muted mt-2">
                    You can always edit everything later from your dashboard.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        {step < 5 && (
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-border">
            <button
              type="button"
              onClick={goBack}
              disabled={step === 1}
              className="btn-secondary text-sm disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Back
            </button>
            <div className="flex items-center gap-3">
              <span className="text-xs text-text-muted">Step {step} of 5</span>
              <button
                type="button"
                onClick={goNext}
                disabled={!canProceed() || saving}
                className="btn-primary text-sm flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Next
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
            <button type="button" onClick={goBack} className="btn-secondary text-sm">
              Back
            </button>
            <span className="text-xs text-text-muted">Step 5 of 5</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- Channel Card Sub-component ---- */
function ChannelCard({
  icon,
  name,
  description,
  connected,
  alwaysOn,
  onToggle,
  children,
}: {
  icon: React.ReactNode;
  name: string;
  description: string;
  connected: boolean;
  alwaysOn?: boolean;
  onToggle?: (v: boolean) => void;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={`card-base card-hover p-4 ${
        connected ? 'border-accent/30 bg-accent/[0.02]' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center ${
              connected ? 'bg-accent/10 text-accent' : 'bg-surface-tertiary text-text-muted'
            }`}
          >
            {icon}
          </div>
          <div>
            <p className="text-sm font-medium text-text-primary">{name}</p>
            <p className="text-xs text-text-secondary mt-0.5">{description}</p>
          </div>
        </div>
        <div className="shrink-0 ml-2">
          {alwaysOn ? (
            <span className="badge badge-green">Active</span>
          ) : (
            <button
              type="button"
              onClick={() => onToggle?.(!connected)}
              className={`relative w-10 h-5 rounded-full transition-colors ${
                connected ? 'bg-accent' : 'bg-surface-tertiary border border-border'
              }`}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  connected ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
